import * as React from 'react';
import { withToastManager } from 'react-toast-notifications';

/**
 * Get a object of react-toast-notifications which will be our notification block.
 *
 * @param  {string}  type
 * @param  {string}  message
 * @return {any}
 */

const messageStatus = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARN: 'warn',
    INFO: 'info',
    REMOVE: 'remove'
};

const notificationDefaultMessage = {
    SUCCESS: 'Successfully completed.',
    ERROR: 'Something went wrong.'
};

export interface INotifyOptions {
    position?: string;
    autoDismis?: boolean;
    pauseOnHover?: boolean;
    autoDismissTimeout?: number;
    placement?: PlacementType; // Feature to be implemented
    transitionDuration?: number;
}

export interface IToastNotification {
    success: (message: string, options?: INotifyOptions) => void;
    error: (message: string, options?: INotifyOptions) => void;
    info: (message: string, options?: INotifyOptions) => void;
    warning: (message: string, options?: INotifyOptions) => void;
    remove: (id: string, callback?: () => unknown) => void;
}

export type INotify = (
    message,
    type,
    options?: INotifyOptions,
    callback?: (id) => void
) => void;

export type PlacementType =
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'
    | 'top-left'
    | 'top-center'
    | 'top-right';

/**
 * Notify HOC is extend from withToastManager for simplified use of notifications
 * Functions that can be used from props are
 *  props.notify
 *      @params message: string
 *      @params type: success|error|warning|info
 *      @params options: INotifyOptions
 *      @params callback: function
 *
 *  props.toastNotification.success
 *      @params message: string
 *      @params options: INotifyOptions
 *      @params callback: function
 *
 *  props.toastNotification.error
 *      @params message: string
 *      @params options: INotifyOptions
 *      @params callback: function
 *
 *  props.toastNotification.warning
 *      @params message: string
 *      @params options: INotifyOptions
 *      @params callback: function
 *
 *  props.toastNotification.info
 *      @params message: string
 *      @params options: INotifyOptions
 *      @params callback: function
 *
 * @param WrappedComponent
 * Component to be wrapped for HOC
 */
export function NotifyHOC(WrappedComponent) {
    const defaultNotificationProps = {
        autoDismiss: true,
        pauseOnHover: true
    };
    return withToastManager(
        class NotifyComponent extends React.Component<any> {
            constructor(props) {
                super(props);
            }

            toast = (
                message,
                type,
                options: INotifyOptions = {},
                callback = (id) => {}
            ) => {
                const { toastManager } = this.props;
                switch (type) {
                    case messageStatus.SUCCESS:
                        return toastManager.add(
                            message || notificationDefaultMessage.SUCCESS,
                            {
                                appearance: 'success',
                                ...defaultNotificationProps,
                                ...options
                            },
                            callback
                        );
                    case messageStatus.ERROR:
                        return toastManager.add(
                            message || notificationDefaultMessage.ERROR,
                            {
                                appearance: 'error',
                                ...options
                            },
                            callback
                        );
                    case messageStatus.WARN:
                    case 'warning':
                        return toastManager.add(
                            message,
                            {
                                appearance: 'warning',
                                ...options
                            },
                            callback
                        );
                    case messageStatus.INFO:
                        return toastManager.add(
                            message,
                            {
                                appearance: 'info',
                                ...options
                            },
                            callback
                        );
                    case messageStatus.REMOVE:
                        toastManager.remove(message);
                        break;
                }
            };

            success = (message, options: INotifyOptions = {}) => {
                const { toastManager } = this.props;
                return toastManager.add(
                    message || notificationDefaultMessage.SUCCESS,
                    {
                        appearance: 'success',
                        ...defaultNotificationProps,
                        ...options
                    }
                );
            };

            error = (message, options: INotifyOptions = {}) => {
                const { toastManager } = this.props;
                return toastManager.add(
                    message || notificationDefaultMessage.ERROR,
                    {
                        appearance: 'error',
                        ...defaultNotificationProps,
                        ...options
                    }
                );
            };

            info = (message, options: INotifyOptions = {}) => {
                const { toastManager } = this.props;
                return toastManager.add(message, {
                    appearance: 'info',
                    ...defaultNotificationProps,
                    ...options
                });
            };

            warning = (message: string, options: INotifyOptions = {}) => {
                const { toastManager } = this.props;
                return toastManager.add(message, {
                    appearance: 'warning',
                    ...defaultNotificationProps,
                    ...options
                });
            };

            remove = (id, callback: () => unknown = () => {}) => {
                const { toastManager } = this.props;
                return toastManager.remove(id, callback);
            };

            render() {
                const { toastManager, ...props } = this.props;
                return (
                    <WrappedComponent
                        {...props}
                        notify={this.toast}
                        toastNotification={{
                            success: this.success,
                            error: this.error,
                            info: this.info,
                            warning: this.warning,
                            remove: this.remove
                        }}
                    />
                );
            }
        }
    );
}

export default NotifyHOC;
